import type { CSSProperties } from "react";
import { Card } from "../ui";
import type { TicketSource, UnifiedTicket } from "./helpdeskTypes";
import "./helpdesk.scss";

const SOURCES: TicketSource[] = ["zammad", "chatwoot", "freepbx"];

// Identity colours, checked for colour-blind separation; the label is always shown.
const SOURCE_COLOR: Record<TicketSource, string> = {
  zammad: "#d9730d",
  chatwoot: "#2f7ed8",
  freepbx: "#0e9f8e",
};

const AGE_BUCKETS = [
  { key: "lt1h", label: "< 1 hour", maxMs: 60 * 60 * 1000 },
  { key: "1to4h", label: "1–4 hours", maxMs: 4 * 60 * 60 * 1000 },
  { key: "4to24h", label: "4–24 hours", maxMs: 24 * 60 * 60 * 1000 },
  { key: "gt24h", label: "> 24 hours", maxMs: Infinity },
] as const;

type AgeBucketKey = (typeof AGE_BUCKETS)[number]["key"];

function sourceLabel(source: TicketSource): string {
  switch (source) {
    case "zammad":
      return "Zammad";
    case "chatwoot":
      return "Chatwoot";
    case "freepbx":
      return "FreePBX";
  }
}

/** Treat terminal / resolved-like statuses as closed; FreePBX CDR rows always count as open. */
function isOpenTicket(ticket: UnifiedTicket): boolean {
  if (ticket.source === "freepbx") return true;
  const s = ticket.status.toLowerCase().trim();
  if (!s) return true;
  return !/\b(closed|resolved|done|merged|complete[sd]?)\b/.test(s);
}

function ageBucketKey(updatedAt: string, nowMs: number): AgeBucketKey {
  const t = Date.parse(updatedAt);
  const ageMs = Number.isFinite(t) ? Math.max(0, nowMs - t) : 0;
  for (const bucket of AGE_BUCKETS) {
    if (ageMs < bucket.maxMs) return bucket.key;
  }
  return "gt24h";
}

function formatRelative(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diffSec = Math.round((nowMs - t) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  try {
    return new Date(t).toLocaleDateString();
  } catch {
    return iso;
  }
}

function buildSourceCounts(openTickets: UnifiedTicket[]): Record<TicketSource, number> {
  const counts: Record<TicketSource, number> = {
    zammad: 0,
    chatwoot: 0,
    freepbx: 0,
  };
  for (const t of openTickets) {
    if (t.source in counts) counts[t.source] += 1;
  }
  return counts;
}

function buildAgeBuckets(openTickets: UnifiedTicket[], nowMs: number): Record<AgeBucketKey, number> {
  const counts: Record<AgeBucketKey, number> = {
    lt1h: 0,
    "1to4h": 0,
    "4to24h": 0,
    gt24h: 0,
  };
  for (const t of openTickets) {
    counts[ageBucketKey(t.updatedAt, nowMs)] += 1;
  }
  return counts;
}

/** Source is identity, not state, so it's a neutral label with a coloured dot. */
export function SourceTag({ source }: { source: TicketSource | string | null | undefined }) {
  const known = source === "zammad" || source === "chatwoot" || source === "freepbx";
  const color = known ? SOURCE_COLOR[source as TicketSource] : undefined;
  return (
    <span className="sq-tag" style={color ? ({ "--tag-color": color } as CSSProperties) : undefined}>
      <i />
      {known ? sourceLabel(source as TicketSource) : source ? String(source) : "Unknown"}
    </span>
  );
}

type Props = {
  tickets: UnifiedTicket[];
  loading?: boolean;
  onSelectTicket?: (ticket: UnifiedTicket) => void;
};

export default function HelpdeskWidgets({ tickets, loading = false, onSelectTicket }: Props) {
  const nowMs = Date.now();
  const openTickets = tickets.filter(isOpenTicket);
  const sourceCounts = buildSourceCounts(openTickets);
  const ageBuckets = buildAgeBuckets(openTickets, nowMs);
  const recent = [...tickets]
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, 8);
  const openTotal = openTickets.length;
  const showEmpty = !loading && tickets.length === 0;
  const waiting = loading && tickets.length === 0;

  return (
    <aside className="sq-stack helpdesk-widgets">
      <Card title="Sources">
        {waiting ? (
          <span className="sq-skel" style={{ width: "70%" }} />
        ) : showEmpty ? (
          <p className="sq-muted mb-0">No open tickets in the current list.</p>
        ) : (
          <ul className="helpdesk-stat-list">
            {SOURCES.map((source) => (
              <li key={source}>
                <SourceTag source={source} />
                <span className="sq-strong">{sourceCounts[source]}</span>
              </li>
            ))}
            <li className="is-total">
              <span className="sq-muted">Open total</span>
              <span className="sq-strong">{openTotal}</span>
            </li>
          </ul>
        )}
      </Card>

      <Card title="Queue summary" subtitle="Age since last update (open tickets)">
        {waiting ? (
          <span className="sq-skel" style={{ width: "70%" }} />
        ) : showEmpty || openTotal === 0 ? (
          <p className="sq-muted mb-0">No open tickets to age.</p>
        ) : (
          <ul className="helpdesk-stat-list">
            {AGE_BUCKETS.map((bucket) => {
              const count = ageBuckets[bucket.key];
              const pct = openTotal > 0 ? Math.round((count / openTotal) * 100) : 0;
              return (
                <li key={bucket.key} className="is-stacked">
                  <span className="helpdesk-stat-row">
                    <span className="sq-muted">{bucket.label}</span>
                    <span className="sq-strong">{count}</span>
                  </span>
                  <span className="sq-meter" aria-hidden="true">
                    {count > 0 ? <i style={{ width: `${pct}%` }} /> : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card flush title="Recent activity">
        {waiting ? (
          <div className="sq-card-body">
            <span className="sq-skel" style={{ width: "70%" }} />
          </div>
        ) : recent.length === 0 ? (
          <p className="sq-muted mb-0 p-3">No recent updates yet.</p>
        ) : (
          <ul className="sq-list">
            {recent.map((ticket) => {
              const content = (
                <span className="sq-list-main">
                  <span className="helpdesk-stat-row">
                    <SourceTag source={ticket.source} />
                    <span className="sq-muted">{formatRelative(ticket.updatedAt, nowMs)}</span>
                  </span>
                  <span className="sq-list-title d-block mt-1">{ticket.title}</span>
                  <span className="sq-list-meta d-block">
                    {ticket.customerLabel || ticket.status}
                  </span>
                </span>
              );
              return (
                <li key={`${ticket.source}-${ticket.id}`}>
                  {onSelectTicket ? (
                    <button
                      type="button"
                      className="sq-list-item is-clickable"
                      onClick={() => onSelectTicket(ticket)}
                    >
                      {content}
                    </button>
                  ) : (
                    <div className="sq-list-item">{content}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </aside>
  );
}
